import { Loader2 } from "lucide-react";

import { BentoCard } from "@/components/ui/BentoCard";
import type { CheckoutKind, PendingBillingAction } from "@/hooks/useBilling";
import {
  CREDIT_PACKS,
  PACK_IDS,
  type PackId,
} from "@domain/billing/credit-packs";
import { formatCredits } from "@domain/billing/format-credits";

const PACK_TO_KIND: Record<PackId, CheckoutKind> = {
  PACK_5: "pack-5",
  PACK_10: "pack-10",
  PACK_20: "pack-20",
  PACK_50: "pack-50",
};

interface CreditsSectionProps {
  balance: string;
  balanceStatus: "loading" | "ready" | "empty" | "negative";
  hasStripeCustomer: boolean;
  pendingAction: PendingBillingAction;
  billingError: string | null;
  onBuyPack: (kind: CheckoutKind) => void;
  onOpenPortal: () => void;
}

/** Pure presentational credits card — all state and actions come in as props. */
export function CreditsSection({
  balance,
  balanceStatus,
  hasStripeCustomer,
  pendingAction,
  billingError,
  onBuyPack,
  onOpenPortal,
}: CreditsSectionProps) {
  const anyPending = pendingAction !== null;

  return (
    <BentoCard shadow className="p-8">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40">
          Credit Balance
        </div>
        <div className="mt-1 text-2xl font-bold text-black dark:text-white">
          {balanceStatus === "loading"
            ? "…"
            : `${formatCredits({ credits: balance })} credits`}
        </div>
      </div>

      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40">
        Buy Credits
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        {PACK_IDS.map((packId) => {
          const pack = CREDIT_PACKS[packId];
          const kind = PACK_TO_KIND[packId];
          const isPending = pendingAction === kind;
          return (
            <button
              key={packId}
              disabled={anyPending}
              onClick={() => onBuyPack(kind)}
              className="flex flex-col items-center rounded-xl border border-black/10 px-4 py-3 transition hover:border-brand-coral/40 hover:bg-brand-coral/5 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10"
            >
              {isPending ? (
                <Loader2 className="h-5 w-5 animate-spin text-brand-coral" />
              ) : (
                <span className="text-lg font-bold text-black dark:text-white">
                  ${pack.priceUsd}
                </span>
              )}
              <span className="text-xs font-medium text-black/50 dark:text-white/50">
                {pack.credits.toLocaleString("en-US")} credits
              </span>
            </button>
          );
        })}
      </div>

      {hasStripeCustomer && (
        <div className="mt-6">
          <button
            disabled={anyPending}
            onClick={onOpenPortal}
            className="flex items-center gap-2 text-sm font-bold text-black/60 underline-offset-4 transition hover:underline disabled:cursor-not-allowed disabled:opacity-40 dark:text-white/60"
          >
            {pendingAction === "portal" && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            Manage billing
          </button>
        </div>
      )}

      {billingError && (
        <p className="mt-4 text-sm font-medium text-brand-coral">
          {billingError}
        </p>
      )}
    </BentoCard>
  );
}
